import http from "Utils/api2";
const { createSlice, createAsyncThunk } = require("@reduxjs/toolkit");

// ─── THUNKS ─────────────────────────────────────────────────────────────────

// GET all cities by admin
export const fetchAllCities = createAsyncThunk(
  "cityManager/fetchAll",
  async (adminId, { rejectWithValue }) => {
    try {
      const res = await http.get(`/getAllCityByAdmin/${adminId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: "Failed to fetch cities" });
    }
  }
);

// POST create city
export const addCity = createAsyncThunk(
  "cityManager/addCity",
  async ({ adminId, data }, { rejectWithValue }) => {
    try {
      const res = await http.post(`/createCity/${adminId}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: "Failed to create city" });
    }
  }
);

// PUT update city
export const editCity = createAsyncThunk(
  "cityManager/editCity",
  async ({ cityId, adminId, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/updateCity/${cityId}/${adminId}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: "Failed to update city" });
    }
  }
);

// PUT toggle active/inactive city
export const toggleCityStatus = createAsyncThunk(
  "cityManager/toggleCity",
  async ({ cityId, adminId }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/disableCity/${cityId}/${adminId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: "Failed to toggle city status" });
    }
  }
);

// DELETE city
export const removeCity = createAsyncThunk(
  "cityManager/removeCity",
  async ({ cityId, adminId }, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/deleteCity/${cityId}/${adminId}`);
      return { ...res.data, cityId };
    } catch (err) {
      return rejectWithValue(err?.response?.data || { message: "Failed to delete city" });
    }
  }
);

// ─── SLICE ───────────────────────────────────────────────────────────────────

const cityCRUDSlice = createSlice({
  name: "cityManager",
  initialState: {
    cities: [],
    loading: false,
    mutating: false, // for create / update / toggle / delete
    error: null,
  },
  reducers: {
    clearCityError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── FETCH ALL ──────────────────────────────────────────
      .addCase(fetchAllCities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCities.fulfilled, (state, action) => {
        state.loading = false;
        state.cities = action.payload?.data || [];
      })
      .addCase(fetchAllCities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to load cities";
      })

      // ── ADD ────────────────────────────────────────────────
      .addCase(addCity.pending, (state) => { state.mutating = true; })
      .addCase(addCity.fulfilled, (state) => { state.mutating = false; })
      .addCase(addCity.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload?.message || "Create failed";
      })

      // ── EDIT ───────────────────────────────────────────────
      .addCase(editCity.pending, (state) => { state.mutating = true; })
      .addCase(editCity.fulfilled, (state) => { state.mutating = false; })
      .addCase(editCity.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload?.message || "Update failed";
      })

      // ── TOGGLE ─────────────────────────────────────────────
      .addCase(toggleCityStatus.pending, (state) => { state.mutating = true; })
      .addCase(toggleCityStatus.fulfilled, (state) => { state.mutating = false; })
      .addCase(toggleCityStatus.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload?.message || "Toggle failed";
      })

      // ── DELETE ─────────────────────────────────────────────
      .addCase(removeCity.pending, (state) => { state.mutating = true; })
      .addCase(removeCity.fulfilled, (state, action) => {
        state.mutating = false;
        // Optimistically remove from local list
        state.cities = state.cities.filter((c) => c._id !== action.payload?.cityId);
      })
      .addCase(removeCity.rejected, (state, action) => {
        state.mutating = false;
        state.error = action.payload?.message || "Delete failed";
      });
  },
});

export const { clearCityError } = cityCRUDSlice.actions;
export default cityCRUDSlice.reducer;
